import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { OrderDeviation } from "@/types";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useGetContractDeviationTypeById, useGetContractDeviationUnits } from "@/services/ContractDeviations";
import { useGetDocumentsFromString } from "@/services/Documents";
import Accordion from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import Loader from "@/components/atoms/Loader";
import DeviationPriceUnitEnum from "@/constants/enums/DeviationPriceUnitEnum";

interface ViewDeviationProps {
  deviations: OrderDeviation[];
}

const ViewDeviation = ({ deviations }: ViewDeviationProps) => {
  return (
    <>
      {deviations.map((deviation) => (
        <ViewDeviationAccordion key={deviation.id} deviation={deviation} />
      ))}
    </>
  );
};

export default ViewDeviation;

const ViewDeviationAccordion = ({ deviation }: { deviation: OrderDeviation }) => {
  const { t } = useTranslation();
  const [isAmountVisible, setIsAmountVisible] = useState(false);
  const [selectedUnitCode, setSelectedUnitCode] = useState<string>("");

  // The tab lists manual deviations, which always have a type; only inspection deviation products have none.
  const { data: deviationType, isLoading: isLoadingDeviationType } = useGetContractDeviationTypeById(
    deviation.contractDeviationTypeId ?? 0,
    { enabled: deviation.contractDeviationTypeId !== null }
  );
  const { data: deviationUnits, isLoading: isLoadingDeviationUnits } = useGetContractDeviationUnits();

  useEffect(() => {
    if (deviationType && deviationUnits) {
      const selectedUnit = deviationUnits.find((unit) => unit.id === deviationType.priceUnit)?.code;
      setSelectedUnitCode(selectedUnit || "");

      if (selectedUnit !== DeviationPriceUnitEnum.PPO.code) setIsAmountVisible(true);
    }
  }, [deviationType, deviationUnits]);

  const PriceText = () => {
    const translatedUnit = t("enumerables:priceUnit." + selectedUnitCode);
    return <Text size="sm">{`${deviationType?.price} ${deviationType?.currency} - ${translatedUnit}`}</Text>;
  };

  const AmountOfText = () => {
    const translatedUnit = t("contracts:contractDeviation.amountOfUnits." + selectedUnitCode);
    return <Text size="sm" color="secondary">{`${translatedUnit}`}</Text>;
  };

  if (isLoadingDeviationType || isLoadingDeviationUnits) {
    return <Loader />;
  }

  return (
    <>
      <Accordion title={`${deviationType?.name} - ${deviationType?.description}`}>
        <Grid p="lg">
          <Grid.Col span={3}>
            <Text size="sm" color="secondary">
              {t("contracts:contractDeviation.deviation")}
            </Text>
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size="sm">{deviationType?.name}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="sm" color="secondary">
              {t("common:description")}
            </Text>
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size="sm">{deviation.description}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="sm" color="secondary">
              {t("common:price")}
            </Text>
          </Grid.Col>
          <Grid.Col span={9}>
            <PriceText />
          </Grid.Col>
          {isAmountVisible && (
            <>
              <Grid.Col span={3}>
                <AmountOfText />
              </Grid.Col>
              <Grid.Col span={9}>
                <Text size="sm">{deviation.amount}</Text>
              </Grid.Col>
            </>
          )}
          <Grid.Col span={3}>
            <Text size="sm" color="secondary">
              {t("common:image")}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            {deviation.imageUuid ? <DeviationImageCarousel imageString={deviation.imageUuid} /> : "-"}
          </Grid.Col>
        </Grid>
      </Accordion>
    </>
  );
};

interface ImageCarouselProps {
  imageString: string;
}

const DeviationImageCarousel = ({ imageString }: ImageCarouselProps) => {
  const { currentCompany } = useAuth();
  const imageQuery = useGetDocumentsFromString([imageString], currentCompany!.companyId);

  return (
    <>
      {imageQuery.pending && <Loader />}
      {!imageQuery.pending && imageQuery.images && (
        <ImageCarousel
          images={imageQuery.images.filter((doc) => {
            return !!doc;
          })}
          imagesPrSlide={1}
          height={90}
        />
      )}
    </>
  );
};
